import { useEffect, useMemo, useState } from 'react'
import { Box } from '@chakra-ui/react'

import { OfferPropertyColumn } from './OfferPropertyColumn'
import { ISelectedRisk, OfferGridProvider } from '../../../context/OfferGridContext'
import { OfferPropertyHeaderColumn } from './OfferPropertyHeaderColumn'
import { OfferHeaderColumnWrapper } from './components/OfferHeaderColumnWrapper'
import { OffersGrid } from './components/OffersGrid'
import { SortBy } from '../../../context/OffersProvider'
import { usePropertyOffersContext } from '../../../context/PropertyOffersProvider'
import { sortByValidFirst } from '../OffersCarList'
import { Loading } from '../../../components/Loading'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { GroupItem, PackageItemModel, Variant } from 'BrokerTsLib/src/dtos'
import { useGetRisks } from '../../../hooks/useEnumProperty'
import { useGetOffersAvailablePackageItems } from '../../../hooks/useOfferPackage'
import { AxiosResponse } from 'axios'
import { UseQueryResult } from 'react-query'

type OfferPropertyGridProps = {
  variants: Variant[]
  icon: string
}

export const OfferPropertyGrid = ({ variants, icon }: OfferPropertyGridProps) => {
  const [openedColumns, setOpenedColumns] = useState<string[]>([])
  const [selectedRiskIds, setSelectedRiskIds] = useState<ISelectedRisk[]>([])

  const { offers, offersIds, sortBy, showAll, isLoading } = usePropertyOffersContext()

  const packageItemsResults = useGetOffersAvailablePackageItems(offersIds)
  const allLoaded = packageItemsResults.findIndex(x => x.isLoading || !x.isFetched)
  const offersAvailablePackageItems = useMemo(() => {
    return packageItemsResults.reduce((acc: { [key: number]: UseQueryResult<AxiosResponse<PackageItemModel[], any>, unknown> }, result, index) => {
      const id = offersIds[index] // Z�sk�me ID pro aktu�ln� v�sledek
      acc[id] = result // P�i�ad�me v�sledek k p��slu�n�mu ID v akumul�toru
      return acc
    }, {})
  }, [allLoaded])

  const [isScrolled, setIsScrolled] = useState(false)

  const handleSectionTitleClick = (sectionId: string) => {
    let newColumns = []

    if (openedColumns.includes(sectionId)) {
      newColumns = openedColumns.filter((column) => column !== sectionId)
    } else {
      newColumns = [...openedColumns, sectionId]
    }

    setOpenedColumns(newColumns)
  }

  const filtered = offers.filter((x) => variants.some(v => v.id == x.insuranceVariantId) && (showAll || x.included))
  var sorted = filtered

  // sortovani
  switch (sortBy) {
    case SortBy.Price:
      sorted = sortByValidFirst(sorted, (a, b) =>
        !!a.price && (!b.price || a.price < b.price) ? -1 : 1,
      )
      break

    case SortBy.Insurer:
      sorted = sortByValidFirst(sorted, (a, b) => a.insurerName.localeCompare(b.insurerName))
      break

    default:
      break
  }

  const handleIsScrolled = (isScrolled: boolean) => {
    setIsScrolled(isScrolled)
  }

  // ziskani stavu pro hromadne akce (hromadne odebrani nebo pridavani rizika)
  // nasetovani vsech rizik do contextu a priznaku, zda je kazde z nich napric nabidkami vybrano nebo ne
  const key = JSON.stringify(filtered.reduce((p: number[], o) => [...p, ...o.parts.filter(p => p.included).map(p => p.packageItem.packageItemId!)], [])) // klic z vybranych part pro detekci zmeny offer party a naslednemu vynuceni prepocitani zda je dane riziko vybrano napric nabidkami nebo nikoli  
  const { data: dataRisks } = useGetRisks()
  useEffect(() => {
    // projizdim postupne vsechna rizika
    const arr = dataRisks?.data.map((groupItem: GroupItem) => {

      // pomocne pole, ktere mi bude udrzovat hodnontu per offeru jestli jsou jeji prislusne package itemy zahrnuty
      const offersRiskSelected: {
        offerId: number,
        selected: boolean,
        addable: boolean
      }[] = []

      // zajimaji nas pouze vyfiltrovane offery
      filtered.forEach(offer => {
        const allOfferPackageItems = offersAvailablePackageItems[offer.offerId]?.data?.data ?? []
        const riskSpecificPackageItems = allOfferPackageItems.filter(x => x.riskId == groupItem.id) ?? []
        const riskSpecificOfferParts = offer.parts.filter(p => p.riskId == groupItem.id)
        const addableRiskSpecificPackageItems = riskSpecificPackageItems.filter(x => x.addable)

        offersRiskSelected.push({
          offerId: offer.offerId,
          // party existuji a jsou vsechny party included
          selected: riskSpecificOfferParts.length == riskSpecificPackageItems.length && !riskSpecificOfferParts.find(p => !p.included),
          addable: addableRiskSpecificPackageItems.length > 0
        })
      })

      return {
        riskId: groupItem.id,
        // true pokud vsechny offery maji true (vsechny offery maji dane riziko zahrnuto)
        selected: offersRiskSelected.filter(x => x.addable).findIndex(x => !x.selected) < 0,
        // pokud zadne riziko nelze pridat ani odebrat, tak se nebude pak vubec zobrazovat moznost hromadne akce
        addable: offersRiskSelected.findIndex(x => x.addable) >= 0
      }
    }) ?? []

    setSelectedRiskIds(arr)
  }, [key, dataRisks?.data.length, offersAvailablePackageItems])

  return (
    <OfferGridProvider
      openedColumns={openedColumns}
      onSectionTitleClick={handleSectionTitleClick}
      risks={selectedRiskIds}
      setRisks={setSelectedRiskIds}
    >
      {(!sorted || sorted.length == 0) && isLoading && <Loading large={true} />}

      {sorted && sorted.length > 0 &&
        <OffersGrid onIsScrolled={handleIsScrolled}>
          <OfferHeaderColumnWrapper isScrolled={isScrolled} icon={icon}>
            <OfferPropertyHeaderColumn variant={variants[0]} />
          </OfferHeaderColumnWrapper>

          <Box>
            {/* <OfferCompleteness passed={passed} /> */}

            <Box sx={{ display: 'flex', justifyContent: 'start' }}>
              {sorted.map((offer) => (
                <LoadingOverlay key={offer.offerId}>
                  <OfferPropertyColumn offer={offer} variant={variants[0]} />
                </LoadingOverlay>
              ))}
            </Box>
          </Box>
        </OffersGrid>
      }
    </OfferGridProvider>
  )
}
